/**
 * Shared category dropdown used by /products and /brands. Backed by
 * `useCategories()` which queries /api/categories (distinct categories
 * present in the tenant's products, sorted alphabetically). Includes a
 * sentinel "All categories" option that emits `null`.
 *
 * Single-select for now. Multi-select is a follow-up if operators ask for it.
 */
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../design';
import { useCategories } from '../lib/categories';

interface CategorySelectProps {
  /** Selected category, or `null` for the "All categories" sentinel. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Optional id to wire a <label htmlFor=...>. */
  id?: string;
  /** Optional className passed to the trigger so callers can size it. */
  className?: string;
}

const ALL_SENTINEL = '__all__';

export function CategorySelect({ value, onChange, id, className }: CategorySelectProps) {
  const { data, isLoading } = useCategories();
  const categories = data?.categories ?? [];
  // Radix Select requires non-empty string values; we map null↔ALL_SENTINEL.
  const selectValue = value ?? ALL_SENTINEL;

  return (
    <Select
      value={selectValue}
      onValueChange={(v) => onChange(v === ALL_SENTINEL ? null : v)}
      disabled={isLoading}
    >
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder={isLoading ? 'Loading…' : 'All categories'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_SENTINEL}>All categories</SelectItem>
        {categories.map((c) => (
          <SelectItem key={c} value={c}>
            {c}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
